import { useState } from 'react'
import { Link } from 'react-router-dom'
import SiteNav from './SiteNav'
import BlogFooter from './BlogFooter'
import SEO from './SEO'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Study {
  n: string
  title: string
  meta: string
  tag: string
  tagColor: 'amber' | 'sand' | 'taupe' | 'green'
}

interface Pillar {
  id: string
  num: string
  title: string
  body: string
  refs: string[]
  studies: Study[]
  exclusive?: boolean
}

// ── Data ──────────────────────────────────────────────────────────────────────

const PILLARS: Pillar[] = [
  {
    id: 'p1',
    num: '01',
    title: "Le corps évalue en premier",
    body: "La neuroception précède la cognition. L'état physiologique détermine ce qui est possible — avant que la pensée consciente intervienne. Ce n'est pas une métaphore : c'est de la neurobiologie autonome documentée. La volonté échoue là où le corps n'est pas d'abord stabilisé.",
    refs: ['Porges, 2001', 'Damasio, 1994', 'Craig, 2002', 'Felitti, 1998'],
    studies: [
      { n: '01', title: 'Polyvagal theory & phylogenetic substrates of a social nervous system', meta: 'Porges, S.W. — Int. Journal of Psychophysiology, 2001', tag: 'Fondateur', tagColor: 'amber' },
      { n: '02', title: 'Descartes\' Error — marqueurs somatiques & décision', meta: 'Damasio, A.R. — Putnam, 1994', tag: 'Fondateur', tagColor: 'amber' },
      { n: '03', title: 'How do you feel? Interoception: the sense of the physiological condition of the body', meta: 'Craig, A.D. — Nature Reviews Neuroscience, 2002', tag: 'Fondateur', tagColor: 'amber' },
      { n: '04', title: 'Relationship of childhood abuse to leading causes of death in adults — ACE Study', meta: 'Felitti, V.J. et al. — Am. J. of Preventive Medicine, 1998 · 17 337 participants', tag: '17 337 sujets', tagColor: 'green' },
    ],
  },
  {
    id: 'p2',
    num: '02',
    title: "Votre état change chaque jour",
    body: "Un outil unique ne peut pas fonctionner tous les jours parce que votre système nerveux n'est pas le même chaque matin. L'évaluation prime sur le protocole. Ce qui régule aujourd'hui peut sur-activer demain. La personnalisation n'est pas un confort — c'est une nécessité biologique.",
    refs: ['Task Force ESC, 1996', 'Kim et al., 2018', 'Lehrer & Gevirtz, 2014', 'Siegel, 1999'],
    studies: [
      { n: '05', title: 'Standards of measurement, physiological interpretation, and clinical use of HRV', meta: 'Task Force ESC/NASPE — Circulation, 1996', tag: 'Référence', tagColor: 'amber' },
      { n: '06', title: 'Stress and heart rate variability: a meta-analysis and review', meta: 'Kim, H.G. et al. — Psychiatry Investigation, 2018 · 121 études', tag: 'Méta-analyse', tagColor: 'green' },
      { n: '07', title: 'HRV biofeedback: how and why it works', meta: 'Lehrer, P.M. & Gevirtz, R. — Frontiers in Psychology, 2014', tag: 'Clinique', tagColor: 'sand' },
      { n: '08', title: 'Window of Tolerance in trauma recovery and regulation', meta: 'Siegel, D.J. — The Developing Mind, Norton, 1999', tag: 'Clinique', tagColor: 'sand' },
    ],
  },
  {
    id: 'p3',
    num: '03',
    title: "La régulation s'apprend",
    body: "Le système nerveux est plastique. Ce qui est pratiqué régulièrement se consolide — au niveau cellulaire, synaptique, structurel. Les gains de régulation persistent bien après l'arrêt de l'entraînement. Le corps apprend la sécurité de la même façon qu'un muscle apprend le mouvement.",
    refs: ['Zaccaro, 2018', 'Hölzel, 2011', 'Brom, 2017', 'Panjabi, 1992', 'van der Kolk, 2014'],
    studies: [
      { n: '09', title: 'How breath-control can change your life: psycho-physiological correlates of slow breathing', meta: 'Zaccaro et al. — Frontiers in Human Neuroscience, 2018', tag: 'Revue systématique', tagColor: 'green' },
      { n: '10', title: 'Brief structured respiration practices enhance mood and reduce physiological arousal', meta: 'Balban, M.Y. et al. — Cell Reports Medicine, Stanford, 2023', tag: '2023', tagColor: 'sand' },
      { n: '11', title: 'Mindfulness practice leads to increases in regional brain gray matter density', meta: 'Hölzel, B.K. et al. — Psychiatry Research, 2011', tag: 'Neuroplasticité', tagColor: 'green' },
      { n: '12', title: 'Somatic Experiencing for PTSD: a randomized controlled outcome study', meta: 'Brom, D. et al. — Psychological Trauma, 2017', tag: 'ECR randomisé', tagColor: 'green' },
      { n: '13', title: 'The stabilizing system of the spine — modèle à 3 sous-systèmes actif/passif/neural', meta: 'Panjabi, M.M. — Journal of Spinal Disorders, 1992', tag: 'Modèle fondateur', tagColor: 'amber' },
      { n: '14', title: 'The Body Keeps the Score: Brain, Mind and Body in the Healing of Trauma', meta: 'van der Kolk, B.A. — Viking, 2014', tag: 'Référence mondiale', tagColor: 'amber' },
    ],
  },
  {
    id: 'p4',
    num: '04',
    title: "L'énergie précède le symptôme",
    body: "Le corps émet des champs mesurables — électromagnétiques, bioélectriques, biophotoniques. La régulation énergétique a une base biophysique documentée. Et le nerf vague est un frein anti-inflammatoire direct — pas seulement un modulateur du stress. Ce pilier est le territoire distinctif d'Amargi.",
    refs: ['Tracey, Nature 2002', 'Pert, 1997', 'McCraty, 2009', 'Rubik, 2015', 'Feinstein, 2025'],
    exclusive: true,
    studies: [
      { n: '15', title: 'The inflammatory reflex — réflexe cholinergique anti-inflammatoire du nerf vague', meta: 'Tracey, K.J. — Nature, 420, 2002', tag: 'Nature', tagColor: 'amber' },
      { n: '16', title: 'Molecules of Emotion — neuropeptides et réseau corps-esprit', meta: 'Pert, C.B. — Scribner, 1997', tag: 'Fondateur', tagColor: 'amber' },
      { n: '17', title: 'The coherent heart: heart-brain interactions, psychophysiological coherence', meta: 'McCraty, R. et al. — HeartMath Institute, 2009', tag: 'Biofield cardiaque', tagColor: 'green' },
      { n: '18', title: 'Biofield science and healing: history, terminology, and concepts', meta: 'Rubik, B. et al. — Global Advances in Health and Medicine, 2015', tag: 'Frontier science', tagColor: 'taupe' },
      { n: '19', title: 'How tapping works: physiological and psychological mechanisms in energy psychology', meta: 'Feinstein, D. — Frontiers in Psychology, 2025 · 200+ essais cliniques', tag: '2025', tagColor: 'sand' },
      { n: '20', title: 'Waking the Tiger: Healing Trauma — énergie de survie et complétion somatique', meta: 'Levine, P.A. — North Atlantic Books, 1997', tag: 'Fondateur SE', tagColor: 'amber' },
    ],
  },
]

const TAG_COLORS: Record<string, string> = {
  amber: 'bg-amber/10 text-amber border border-amber/20',
  sand: 'bg-sand/10 text-sand border border-sand/20',
  taupe: 'bg-taupe/10 text-taupe border border-taupe/20',
  green: 'bg-[taupe]/10 text-[taupe] border border-[taupe]/20',
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function PillarCard({ pillar }: { pillar: Pillar }) {
  const isP4 = pillar.exclusive
  return (
    <div className={`p-8 md:p-10 border border-sand/30 transition-colors duration-200 ${isP4 ? 'bg-sand/8 border-t-2 border-t-amber' : 'bg-paper hover:bg-sand/5'}`}>
      <div className="flex items-start justify-between mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber">Pilier {pillar.num}</span>
        {isP4 && (
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber text-paper px-2.5 py-1 rounded-sm">
            Territoire Amargi
          </span>
        )}
      </div>
      <h3 className="font-serif text-[22px] font-semibold text-ink leading-snug mb-4">
        {pillar.title}
      </h3>
      <p className="text-[14px] text-taupe leading-[1.75] mb-6">
        {pillar.body}
      </p>
      <div className="border-t border-sand/30 pt-4 flex flex-wrap gap-2">
        {pillar.refs.map(r => (
          <span key={r} className="text-[11px] text-taupe/70 font-medium">{r}</span>
        ))}
      </div>
    </div>
  )
}

function LibraryPillar({ pillar, defaultOpen }: { pillar: Pillar; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen ?? false)
  const isP4 = pillar.exclusive

  return (
    <div className={`border border-sand/30 rounded-xl overflow-hidden transition-all duration-200 ${isP4 ? 'border-amber/30' : ''}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-sand/5 transition-colors"
        aria-expanded={open}
      >
        <span className={`text-[13px] font-bold tabular-nums w-8 shrink-0 ${isP4 ? 'text-amber' : 'text-amber'}`}>
          {isP4 ? '04 ★' : `0${pillar.num}`}
        </span>
        <span className="flex-1 font-medium text-ink text-[15px]">{pillar.title}</span>
        <span className="text-[12px] text-taupe/60 shrink-0">{pillar.studies.length} études</span>
        <svg
          className={`w-4 h-4 text-taupe/50 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-sand/20 divide-y divide-sand/15">
          {pillar.studies.map(s => (
            <div key={s.n} className="flex items-start gap-4 px-6 py-4 hover:bg-sand/5 transition-colors">
              <span className="text-[12px] font-bold text-amber/60 tabular-nums w-6 shrink-0 mt-0.5">{s.n}</span>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-medium text-ink leading-snug mb-1">{s.title}</p>
                <p className="text-[12px] text-taupe/70 leading-relaxed">{s.meta}</p>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${TAG_COLORS[s.tagColor]}`}>
                {s.tag}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SciencePage() {
  return (
    <div className="bg-paper min-h-screen">
      <SEO
        title="La Science — Amargi"
        description="Chaque protocole Amargi trace une ligne directe vers un texte fondateur. 4 piliers, 20 études, 80 ans de recherche sur la régulation du système nerveux."
        path="/fr/science"
      />
      <SiteNav />

      <main className="pt-20">

        {/* ── HERO ─────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden py-24 md:py-32">
          {/* Radial glow */}
          <div className="pointer-events-none absolute -top-20 right-0 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(196,137,42,0.055)_0%,transparent_68%)]" />

          <div className="max-w-[600px] mx-auto px-6 md:px-8">
            {/* Opening quote */}
            <blockquote className="border-l-2 border-amber pl-5 mb-10 font-serif text-[18px] italic text-taupe leading-[1.7]">
              Vous avez essayé de vous réguler.<br />
              Ça n'a pas tenu. Voici pourquoi —<br />
              et ce qui change quand on part du corps.
            </blockquote>

            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber mb-4">
              La science derrière Amargi
            </p>

            <h1 className="font-serif text-[clamp(32px,5vw,52px)] font-bold text-ink leading-[1.1] tracking-tight mb-6">
              Ce que nous faisons<br />
              est ancré dans la <em className="not-italic text-amber font-normal">biologie.</em><br />
              Pas dans la croyance.
            </h1>

            <p className="text-[16px] text-taupe leading-[1.78] mb-10 max-w-xl">
              Amargi n'est pas "inspiré par la recherche." Chaque protocole, chaque évaluation, chaque intervention trace une ligne directe vers un texte fondateur. Voici ces fondations.
            </p>

            <div className="flex flex-wrap gap-4 items-center mb-14">
              <Link
                to="/fr/evaluation"
                className="inline-block bg-lapis text-paper text-[13px] font-medium tracking-wide px-8 py-3.5 rounded-sm hover:bg-ink transition-colors"
              >
                Accéder à l'outil de régulation
              </Link>
              <a
                href="#pillars"
                className="text-[13px] text-ink border-b border-ink/30 pb-0.5 hover:text-amber hover:border-amber transition-colors"
              >
                Voir les 4 piliers →
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-10">
              {[
                { num: '20', label: 'Textes fondateurs' },
                { num: '4', label: 'Piliers scientifiques' },
                { num: '80 ans', label: 'De recherche couverts' },
              ].map(s => (
                <div key={s.label}>
                  <div className="font-serif text-[28px] font-bold text-ink leading-none mb-1">{s.num}</div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-taupe/70">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 4 PILLARS ────────────────────────────────────────────────────── */}
        <section id="pillars" className="bg-sand/8 py-20 md:py-28">
          <div className="max-w-[1100px] mx-auto px-6 md:px-8">
            <div className="mb-14">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber mb-3">Architecture scientifique</p>
              <h2 className="font-serif text-[clamp(24px,3.5vw,36px)] font-bold text-ink leading-[1.2] tracking-tight mb-4">
                Quatre piliers.<br />Un seul fil rouge.
              </h2>
              <p className="text-[15px] text-taupe leading-[1.75] max-w-lg">
                Pourquoi la volonté ne suffit pas. Pourquoi le même outil ne fonctionne pas tous les jours. Pourquoi le corps sait avant le cerveau. Et pourquoi l'énergie précède le symptôme.
              </p>
            </div>

            {/* 2×2 grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 border border-sand/30 divide-y md:divide-y-0 md:divide-x divide-sand/30">
              {PILLARS.map(p => <PillarCard key={p.id} pillar={p} />)}
            </div>

            {/* CTA band */}
            <div className="mt-12 bg-ink rounded-sm px-8 py-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h3 className="font-serif text-[19px] font-bold text-paper mb-1">
                  Votre système nerveux, évalué en quelques minutes
                </h3>
                <p className="text-[13px] text-paper/55">L'outil Amargi applique ces 4 piliers à votre état du jour. Pas de protocole générique.</p>
              </div>
              <Link
                to="/fr/evaluation"
                className="shrink-0 inline-block bg-lapis text-paper text-[13px] font-medium tracking-wide px-7 py-3 rounded-sm hover:bg-paper hover:text-ink transition-colors whitespace-nowrap"
              >
                Accéder à l'outil →
              </Link>
            </div>
          </div>
        </section>

        {/* ── INSIDE THE TOOL ──────────────────────────────────────────────── */}
        <section id="outil" className="border-y border-sand/30 py-20 md:py-28">
          <div className="max-w-[1100px] mx-auto px-6 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">

              {/* Text */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber mb-4">Transparence radicale</p>
                <h2 className="font-serif text-[28px] font-bold text-ink leading-[1.2] tracking-tight mb-5">
                  Chaque étape<br />montre sa source
                </h2>
                <p className="text-[14px] text-taupe leading-[1.75] mb-4">
                  Dans l'outil Amargi, chaque dimension mesurée est reliée à la recherche qui la fonde. Le score "cohérence respiratoire" cite Zaccaro 2018. Le score "charge allostasique" cite McEwen 1998.
                </p>
                <p className="text-[14px] text-taupe leading-[1.75] mb-6">
                  Pas de main-waving. Pas de "selon les recherches." Des citations réelles, dans l'outil — parce que comprendre pourquoi on vous demande ce qu'on vous demande fait partie de la régulation elle-même.
                </p>
                <Link
                  to="/fr/evaluation"
                  className="text-[13px] text-ink border-b border-ink/30 pb-0.5 hover:text-amber hover:border-amber transition-colors"
                >
                  Accéder à l'outil →
                </Link>
              </div>

              {/* Mockup */}
              <div className="bg-ink rounded-sm overflow-hidden">
                <div className="flex gap-1.5 px-4 py-3 border-b border-paper/8">
                  <div className="w-2.5 h-2.5 rounded-full bg-paper/15" />
                  <div className="w-2.5 h-2.5 rounded-full bg-paper/15" />
                  <div className="w-2.5 h-2.5 rounded-full bg-paper/15" />
                </div>
                {[
                  { label: 'Cohérence respiratoire', cite: '→ Zaccaro et al., Frontiers in Human Neuroscience, 2018', pct: 72, score: '7.2' },
                  { label: 'Tonus vagal & HRV', cite: '→ Task Force ESC/NASPE, Circulation, 1996', pct: 55, score: '5.5' },
                  { label: 'Charge allostasique', cite: '→ McEwen, B.S., Ann. N.Y. Acad. Sci., 1998', pct: 38, score: '3.8' },
                  { label: 'Cohérence énergétique', cite: '→ McCraty, R., HeartMath Institute, 2009', pct: 61, score: '6.1' },
                ].map(row => (
                  <div key={row.label} className="px-5 py-4 border-b border-paper/6 last:border-0">
                    <p className="text-[9px] uppercase tracking-[0.15em] text-paper/35 mb-1">Dimension évaluée</p>
                    <p className="text-[13px] font-medium text-paper mb-1">{row.label}</p>
                    <p className="text-[10px] text-amber/70 mb-2.5">{row.cite}</p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1 bg-paper/10 rounded-full overflow-hidden">
                        <div className="h-full bg-amber rounded-full" style={{ width: `${row.pct}%` }} />
                      </div>
                      <span className="text-[13px] font-bold text-paper tabular-nums">{row.score}</span>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* ── BIBLIOTHÈQUE ─────────────────────────────────────────────────── */}
        <section id="bibliotheque" className="py-20 md:py-28">
          <div className="max-w-[600px] mx-auto px-6 md:px-8">
            <div className="mb-14">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber mb-3">La bibliothèque complète</p>
              <h2 className="font-serif text-[clamp(24px,3.5vw,36px)] font-bold text-ink leading-[1.2] tracking-tight mb-4">
                20 textes fondateurs.<br />Zéro approximation.
              </h2>
              <p className="text-[15px] text-taupe leading-[1.75]">
                Les travaux qui ont informé la conception d'Amargi, organisés par pilier. Ouvrez ce qui vous intéresse.
              </p>
            </div>

            <div className="space-y-3">
              {PILLARS.map((p, i) => (
                <LibraryPillar key={p.id} pillar={p} defaultOpen={i === 0} />
              ))}
            </div>
          </div>
        </section>

        {/* ── TÉMOIGNAGES ──────────────────────────────────────────────────── */}
        <section className="bg-sand/8 py-20 border-t border-sand/30">
          <div className="max-w-[1100px] mx-auto px-6 md:px-8">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber mb-10">Ce que les clients disent</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { text: "Ce que j'ai compris en travaillant avec Amargi, c'est que mon corps savait déjà. On lui a redonné la parole — et la permission d'être entendu.", author: '— Cliente, accompagnement individuel, 2024' },
                { text: "Je pensais que mes problèmes de concentration étaient un problème de discipline. C'était un problème de système nerveux. La différence entre les deux, c'est toute la pratique Amargi.", author: '— Dirigeant, programme Cohérence, 2024' },
                { text: "Pour la première fois, quelqu'un m'a expliqué ce qui se passait dans mon corps avec des mots vrais. Pas des métaphores. Pas du mysticisme. Juste de la biologie. Ça a tout changé.", author: '— Cliente, bilan de résilience, 2023' },
              ].map((t, i) => (
                <div key={i} className="bg-paper rounded-sm p-6 relative">
                  <span className="absolute top-3 left-4 font-serif text-[52px] text-amber/12 leading-none select-none">"</span>
                  <p className="font-serif text-[15px] italic text-ink leading-[1.72] pt-5 mb-5">{t.text}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-taupe/60">{t.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── AMARGI — signature philosophique ─────────────────────────────── */}
        <section className="bg-ink py-24 md:py-32 relative overflow-hidden">
          {/* Cuneiform watermark */}
          <div className="pointer-events-none select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[320px] text-paper/[0.025] leading-none">
            𒀭
          </div>
          <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber mb-5">Pourquoi ce nom</p>
                <div className="text-[40px] text-paper/25 tracking-[0.2em] mb-5 leading-none" aria-hidden="true">𒀭𒂗𒍪</div>
                <h2 className="font-serif text-[clamp(22px,3vw,34px)] font-normal italic text-paper leading-[1.25] mb-3">
                  "Retour à la mère."<br />Retour à l'état originel.
                </h2>
                <p className="text-[10px] uppercase tracking-[0.2em] text-paper/25 mt-4">
                  2300 av. J.-C. · Lagash, Sumérie<br />
                  Premier mot écrit de l'histoire humaine pour la liberté
                </p>
              </div>
              <div className="font-serif text-[18px] leading-[1.82] text-paper/58 space-y-5">
                <p>
                  <strong className="text-paper/88 font-medium">Amargi</strong> est le premier mot écrit de l'histoire humaine pour désigner la liberté. Gravé en cunéiforme à Lagash, en Sumérie. Il désignait la libération de la dette, du joug — le retour à un état antérieur à la charge.
                </p>
                <p>
                  C'est exactement ce que fait la régulation nerveuse. Elle ne crée pas quelque chose de nouveau. <strong className="text-paper/88 font-medium">Elle restaure ce qui était là avant la surcharge, avant le trauma, avant la dissociation.</strong> Le système nerveux n'oublie pas son état optimal — il a simplement perdu le chemin pour y revenir.
                </p>
                <p>Amargi, c'est ce chemin.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── DISCLAIMER ───────────────────────────────────────────────────── */}
        <section className="bg-ink border-t border-paper/6 py-8">
          <div className="max-w-[600px] mx-auto px-6 md:px-8">
            <div className="flex gap-4 items-start">
              <svg className="w-4 h-4 shrink-0 text-paper/25 mt-0.5" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.4">
                <circle cx="9" cy="9" r="7.5" />
                <path d="M9 5.5v5M9 12.5h.01" />
              </svg>
              <p className="text-[12px] text-paper/28 leading-[1.7]">
                Amargi est un espace de régulation somatique et d'accompagnement ancré en coaching et en recherche publiée. Il ne constitue pas un diagnostic médical, une thérapie clinique, ni un traitement de trouble mental. Si vous traversez une crise ou avez besoin d'un soutien clinique, consultez un professionnel de santé agréé.
              </p>
            </div>
          </div>
        </section>

        {/* ── CTA FINAL ────────────────────────────────────────────────────── */}
        <section className="bg-ink py-24 md:py-32 text-center relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(196,137,42,0.06)_0%,transparent_65%)]" />
          <div className="relative z-10 max-w-[520px] mx-auto px-6 md:px-8">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber mb-4">L'après commence ici</p>
            <h2 className="font-serif text-[clamp(26px,4vw,42px)] font-bold text-paper leading-[1.2] tracking-tight mb-5">
              Votre système nerveux<br />mérite mieux que la volonté.
            </h2>
            <p className="text-[15px] text-paper/45 leading-[1.72] mb-10">
              Commencez par l'outil de régulation en ligne. Ou travaillez directement avec Bertrand au centre Amargi.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/fr/evaluation"
                className="inline-block bg-lapis text-paper text-[13px] font-medium tracking-wide px-10 py-4 rounded-sm hover:bg-paper hover:text-ink transition-colors"
              >
                Accéder à l'outil de régulation
              </Link>
              <a
                href="https://www.coherence.be"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block border border-paper/20 text-paper/60 text-[13px] font-medium tracking-wide px-10 py-4 rounded-sm hover:border-paper/45 hover:text-paper/85 transition-colors"
              >
                Réserver une séance au centre
              </a>
            </div>
            <p className="text-[11px] text-paper/25 mt-6 tracking-wide">
              L'outil est accessible immédiatement · Le centre est basé à Rosières, Belgique
            </p>
          </div>
        </section>

      </main>

      <BlogFooter />
    </div>
  )
}
